import { CategoryIcon, reminderLook } from './CategoryIcon'
import { formatDistance } from '../lib/location'
import type { Reminder } from '../lib/api'
import { useStore } from '../lib/store'
import './ReminderCard.css'

export function ReminderCard({
  reminder,
  nearby,
  index,
  onToggle,
  onComplete,
}: {
  reminder: Reminder
  nearby: boolean
  index: number
  onToggle: (r: Reminder) => void
  onComplete: (r: Reminder) => void
}) {
  const categories = useStore((s) => s.categories)
  const cat = reminderLook(reminder, categories)
  const store = reminder.nearest_store
  const closest = reminder.closest_member

  // "You're nearby" replaces the distance — the mockup's key state change.
  let subline: React.ReactNode
  if (nearby) {
    subline = <span className="rc__nearby">You&rsquo;re nearby</span>
  } else if (closest && !closest.is_you) {
    subline = <span className="rc__closest">{closest.display_name} is closest</span>
  } else if (store?.distance_m != null) {
    subline = formatDistance(store.distance_m)
  } else {
    subline = <span className="rc__pending">Looking for stores&hellip;</span>
  }

  const closedNow = store && store.is_open === false

  return (
    <article
      className={`rc glass rise${nearby ? ' rc--nearby' : ''}${
        reminder.active ? '' : ' rc--off'
      }`}
      style={
        {
          animationDelay: `${Math.min(index, 8) * 55}ms`,
          '--cat': cat.color,
        } as React.CSSProperties
      }
    >
      <button
        className="rc__icon"
        onClick={() => onComplete(reminder)}
        aria-label={`Mark ${reminder.title} done`}
        title="Mark done"
      >
        <CategoryIcon icon={cat.icon} color={cat.color} />
      </button>

      <div className="rc__body">
        <h3>{reminder.title}</h3>
        <p className="rc__store">
          {reminder.merchant_label}
          {closedNow && <span className="rc__closed">closed now</span>}
        </p>
        <p className="rc__meta">{subline}</p>
      </div>

      {nearby && <span className="rc__pulse" aria-hidden="true" />}

      <button
        role="switch"
        aria-checked={reminder.active}
        aria-label={`${reminder.active ? 'Pause' : 'Resume'} ${reminder.title}`}
        className="rc__switch"
        onClick={() => onToggle(reminder)}
      >
        <span />
      </button>
    </article>
  )
}
